/// <reference types="vite/client" />

import { createClient, SupabaseClient } from '@supabase/supabase-js';
import React, { createContext, useContext, useMemo } from 'react';
import { requireAppSchema } from '../lib/schema';

// Supabase configuration from environment variables
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
// Postgres schema the app reads/writes (learning_diversity). Fails closed: an
// unset or 'public' value throws, because on SCR Central 'public' belongs to
// other applications. Vite inlines VITE_* values at build time.
const supabaseDbSchema = requireAppSchema(
  import.meta.env.VITE_SUPABASE_DB_SCHEMA,
  'VITE_SUPABASE_DB_SCHEMA',
);

// Validate environment variables
if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    'Missing Supabase environment variables. Please check your .env file and ensure VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY are set.'
  );
}

// Create Supabase client
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  db: { schema: supabaseDbSchema },
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true
  }
});

// Define the type for the Supabase context
interface SupabaseContextType {
  supabase: SupabaseClient;
}

// Create the context with a default value
const SupabaseContext = createContext<SupabaseContextType | null>(null);

// Supabase provider component
interface SupabaseProviderProps {
  children: React.ReactNode;
}

export const SupabaseProvider: React.FC<SupabaseProviderProps> = ({
  children
}) => {
  const value = useMemo(() => ({ supabase }), []);

  return (
    <SupabaseContext.Provider value={value}>
      {children}
    </SupabaseContext.Provider>
  );
};

// Custom hook to use Supabase
export const useSupabase = (): SupabaseClient => {
  const context = useContext(SupabaseContext);
  if (!context) {
    throw new Error('useSupabase must be used within a SupabaseProvider');
  }
  return context.supabase;
};

// Export the client for direct use when needed
export default supabase;
